import { useState, useMemo, useCallback } from 'react';
import { useProjectBoard } from '../hooks/useProjectBoard';
import { useLayout } from '../hooks/useLayout';
import { startGitHubOAuth } from '../utils/github';
import styles from './TaskBoard.module.css';

interface BoardItem {
  id: string;
  title: string;
  number: number;
  url: string;
  state: string;
  repo: string;
  status: string;
  labels: { name: string; color: string }[];
}

/** Card component shared between desktop and mobile */
function Card({ item }: { item: BoardItem }) {
  const isClosed = item.state === 'CLOSED' || item.state === 'MERGED';
  return (
    <a
      href={item.url}
      target="_blank"
      rel="noopener noreferrer"
      className={`${styles.card} ${isClosed ? styles.cardStateClosed : ''}`}
    >
      <div className={styles.cardTop}>
        <span className={styles.cardTitle}>{item.title}</span>
        <span className={styles.cardNumber}>#{item.number}</span>
      </div>
      <div className={styles.cardMeta}>
        <span className={styles.cardRepo}>{item.repo.split('/')[1]}</span>
        {item.labels.map((label) => (
          <span
            key={label.name}
            className={styles.cardLabel}
            style={{ backgroundColor: `#${label.color}` }}
          >
            {label.name}
          </span>
        ))}
      </div>
    </a>
  );
}

/** Desktop: horizontal Kanban columns */
function DesktopBoard({ columns, itemsByColumn }: {
  columns: string[];
  itemsByColumn: Map<string, BoardItem[]>;
}) {
  return (
    <div className={styles.boardDesktop}>
      {columns.map((col) => {
        const items = itemsByColumn.get(col) ?? [];
        return (
          <div key={col} className={styles.column}>
            <div className={styles.columnHeader}>
              <span className={styles.columnTitle}>{col}</span>
              <span className={styles.columnCount}>{items.length}</span>
            </div>
            <div className={styles.columnBody}>
              {items.length === 0 ? (
                <div className={styles.emptyColumn}>No items</div>
              ) : (
                items.map((item) => <Card key={item.id} item={item} />)
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/** Mobile: collapsible accordion sections */
function MobileBoard({ columns, itemsByColumn }: {
  columns: string[];
  itemsByColumn: Map<string, BoardItem[]>;
}) {
  // Default: expand columns that have items
  const [expanded, setExpanded] = useState<Set<string>>(() => {
    const initial = new Set<string>();
    for (const col of columns) {
      const items = itemsByColumn.get(col);
      if (items && items.length > 0) initial.add(col);
    }
    return initial;
  });

  const toggle = (col: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(col)) next.delete(col);
      else next.add(col);
      return next;
    });
  };

  return (
    <div className={styles.boardMobile}>
      {columns.map((col) => {
        const items = itemsByColumn.get(col) ?? [];
        const isOpen = expanded.has(col);
        return (
          <div key={col} className={styles.section}>
            <div className={styles.sectionHeader} onClick={() => toggle(col)}>
              <div className={styles.sectionLeft}>
                <span className={`${styles.sectionChevron} ${isOpen ? styles.sectionChevronOpen : ''}`}>
                  ▶
                </span>
                <span className={styles.columnTitle}>{col}</span>
              </div>
              <span className={styles.columnCount}>{items.length}</span>
            </div>
            {isOpen && (
              <div className={styles.sectionBody}>
                {items.length === 0 ? (
                  <div className={styles.emptyColumn}>No items</div>
                ) : (
                  items.map((item) => <Card key={item.id} item={item} />)
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/** TaskBoard — self-contained data-fetching board component */
export function TaskBoard() {
  const { board, loading, error, needsGitHub, needsProject, refresh } = useProjectBoard();
  const { isMobile } = useLayout();
  const [oauthError, setOauthError] = useState<string | null>(null);

  const handleConnectGitHub = useCallback(async () => {
    try {
      setOauthError(null);
      await startGitHubOAuth();
    } catch (err) {
      setOauthError(err instanceof Error ? err.message : 'Failed to start OAuth');
    }
  }, []);

  // Group items by status column
  const itemsByColumn = useMemo(() => {
    const map = new Map<string, BoardItem[]>();
    if (!board) return map;
    for (const col of board.columns) {
      map.set(col, []);
    }
    for (const item of board.items) {
      const col = map.has(item.status) ? item.status : 'Backlog';
      const list = map.get(col);
      if (list) list.push(item);
    }
    return map;
  }, [board]);

  // GitHub not connected — show connect prompt
  if (needsGitHub) {
    return (
      <div className={styles.container}>
        <div className={styles.setupState}>
          <span className={styles.setupIcon}>🔗</span>
          <span className={styles.setupTitle}>Connect your GitHub account to view the board</span>
          <button className={styles.setupBtn} onClick={() => void handleConnectGitHub()}>
            Connect GitHub
          </button>
          {oauthError && <span className={styles.setupError}>{oauthError}</span>}
        </div>
      </div>
    );
  }

  // No project selected — show project prompt
  if (needsProject) {
    return (
      <div className={styles.container}>
        <div className={styles.setupState}>
          <span className={styles.setupIcon}>📋</span>
          <span className={styles.setupTitle}>Select a project board in Settings</span>
          <span className={styles.setupHint}>
            Open Settings → GitHub to choose a project
          </span>
        </div>
      </div>
    );
  }

  if (loading && !board) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingState}>
          <div className={styles.spinner} />
          <span>Loading board…</span>
        </div>
      </div>
    );
  }

  if (error && !board) {
    return (
      <div className={styles.container}>
        <div className={styles.errorState}>
          <span>Failed to load board</span>
          <span style={{ fontSize: 12, color: '#666' }}>{error}</span>
          <button className={styles.retryBtn} onClick={() => void refresh()}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!board) return null;

  return (
    <div className={styles.container}>
      {isMobile ? (
        <MobileBoard columns={board.columns} itemsByColumn={itemsByColumn} />
      ) : (
        <DesktopBoard columns={board.columns} itemsByColumn={itemsByColumn} />
      )}
    </div>
  );
}
